<template>
  <div
    v-click-outside="hideMultiSelectItems"
    class="multiselect-box"
  >
    <button
      class="multiselect-box__select"
      :disabled="disabled"
      :class="{ 'multiselect-box__select--disabled': disabled, 'multiselect-box__select--active': selectActive && !errorMessage, 'multiselect-box__select--error': errorMessage }"
      type="button"
      @click="selectActive = !selectActive;"
    >
      {{ selectedItemsLabel }}
      <v-icon
        v-show="!errorMessage"
        :size="24"
        class="ml-auto"
        :color="disabled ? 'secondaryLight' : 'secondaryMedium'"
      >
        $icon_arrow_down
      </v-icon>
      <v-icon
        v-show="errorMessage"
        :size="24"
        color="negative"
        class="ml-auto"
      >
        $icon_attention
      </v-icon>
    </button>
    <div
      v-show="selectActive"
      class="multiselect-box__content multiselect-box__items"
    >
      <div
        class="multiselect-box__item"
      >
        <CheckBox
          v-model="isSelectedAll"
          :label="labelStrings.allLabel"
          :indeterminate="isIndeterminate"
          class="multiselect-box__item__checkbox"
        />
      </div>
      <div
        v-for="item in items"
        :key="item.value"
        class="multiselect-box__item"
        @click="clickItem(item)"
      >
        <CheckBox
          :label="item.text"
          :value="selectedItems?.some(selectedItem => selectedItem.value === item.value)"
          class="multiselect-box__item__checkbox"
          @change="clickItem(item)"
        />
      </div>
    </div>
    <div
      v-show="errorMessage"
      class="multiselect-box__error-message"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import CheckBox from "../../selection-controls/CheckBox.vue";
import { SelectBoxItem, LabelStrings } from '../types';
import { clickItemHandler, selectedItemsLabelHandler } from "../multiSelectUtils";

const props = defineProps<{
  disabled?: boolean,
  value?: SelectBoxItem[],
  labelStrings: LabelStrings,
  items: SelectBoxItem[],
  errorMessage?: string,
}>();

const emit = defineEmits<{
  (e: "input", value?: SelectBoxItem[]): void;
}>();

const selectActive = ref<boolean>(false);
const inRange = (num: number, a: number, b=0) => (Math.min(a,b) <= num && num < Math.max(a,b));
const isIndeterminate = ref(props?.value && inRange(props?.value?.length, 1, props.items.length));

const isSelectedAll = computed({
  get: () => props?.value?.length === props.items.length,
  set: (newValue) => {
    selectedItems.value = newValue ? props.items : [];
  }
})

const selectedItems = computed({
  get: () => props.value ? props.value : [],
  set: (newValue) => {
    isIndeterminate.value = inRange(newValue.length, 1, props.items.length);
    emit('input', newValue);
  }
})

const selectedItemsLabel = computed(() =>
  selectedItemsLabelHandler(isSelectedAll.value, selectedItems.value, props.labelStrings)
);

const hideMultiSelectItems = () => selectActive.value = false;

const clickItem = (item: SelectBoxItem) => selectedItems.value = clickItemHandler(item, selectedItems.value);


</script>

<style lang="scss" scoped>
@import "../../../assets/styles/main";
@import "../multiSelectStyles";
.multiselect-box {
  &__item {
    padding: 2px 0;
    &:hover {
      background: $accentClear;
    }
  }
  &__select{
    color: $primary;
  }
}

::v-deep .multiselect-box__items{
  .eewc-checkbox--normal svg{
    color: $accent;
  }
}

</style>